import { useCallback, useEffect, useMemo, useState } from "react";
import { listAuditLog } from "../../../../api/adminAuditApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import AdminTableFooter from "../layout/AdminTableFooter";
import RequirePermission from "../../../../auth/RequirePermission";
import StatusBadge from "../../intelligence/StatusBadge";
import { formatAuditAction } from "../../../../utils/formatAuditAction";
import { formatDateTime } from "../users/adminUserFormatters";

const PAGE_SIZE = 50;

function OutcomeBadge({ outcome }) {
  if (!outcome) {
    return <span className="muted">—</span>;
  }

  const tone =
    outcome === "success" ? "confirmed" : outcome === "denied" ? "disputed" : "medium";

  return <StatusBadge label={outcome} tone={tone} />;
}

function SeverityBadge({ severity }) {
  if (!severity) {
    return <span className="muted">—</span>;
  }

  const tone =
    severity === "critical" ? "severe" : severity === "warning" ? "medium" : "neutral";

  return <StatusBadge label={severity} tone={tone} />;
}

function AuditDetailModal({ entry, onClose }) {
  if (!entry) {
    return null;
  }

  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Audit</p>
            <h2>{formatAuditAction(entry.action)}</h2>
            <p className="muted">{formatDateTime(entry.createdAt)}</p>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="admin-re-modal-body">
          <dl className="admin-users-detail">
            <div>
              <dt>User</dt>
              <dd>
                {entry.userDisplayName || entry.userEmail || "—"}
                {entry.userEmail ? (
                  <div className="muted admin-re-source-code">{entry.userEmail}</div>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>Outcome</dt>
              <dd>
                <OutcomeBadge outcome={entry.outcome} />
              </dd>
            </div>
            <div>
              <dt>Severity</dt>
              <dd>
                <SeverityBadge severity={entry.severity} />
              </dd>
            </div>
            <div>
              <dt>Entity</dt>
              <dd>
                {entry.entityType || "—"}
                {entry.entityId ? (
                  <div className="muted admin-re-source-code">{entry.entityId}</div>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>IP address</dt>
              <dd>{entry.ipAddress || "—"}</dd>
            </div>
          </dl>
          {entry.metadata ? (
            <section className="admin-audit-metadata">
              <h3>Metadata</h3>
              <pre className="admin-audit-metadata__json">
                {JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const AdminAuditLogPageContent = ({ onClose }) => {
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);

  const loadAuditLog = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await listAuditLog({
        q: search.trim() || undefined,
        action: actionFilter || undefined,
        outcome: outcomeFilter || undefined,
        severity: severityFilter || undefined,
        entityType: entityTypeFilter || undefined,
        page,
        pageSize: PAGE_SIZE,
        sort: "created_at",
        direction: "desc",
      });
      setEntries(body.data || []);
      setPagination(body.pagination || { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 });
    } catch (error) {
      setEntries([]);
      setErrorMessage(error.message || "Unable to load audit log.");
    } finally {
      setIsLoading(false);
    }
  }, [search, actionFilter, outcomeFilter, severityFilter, entityTypeFilter, page]);

  useEffect(() => {
    loadAuditLog();
  }, [loadAuditLog]);

  const kpis = useMemo(() => {
    const total = pagination.total;
    const successCount = entries.filter((e) => e.outcome === "success").length;
    const failureCount = entries.filter((e) => e.outcome === "failure").length;
    const deniedCount = entries.filter((e) => e.outcome === "denied").length;
    const criticalCount = entries.filter((e) => e.severity === "critical").length;
    const warningCount = entries.filter((e) => e.severity === "warning").length;

    return { total, successCount, failureCount, deniedCount, criticalCount, warningCount };
  }, [entries, pagination.total]);

  const hasActiveFilters = search || actionFilter || outcomeFilter || severityFilter || entityTypeFilter;

  const clearFilters = () => {
    setSearch("");
    setActionFilter("");
    setOutcomeFilter("");
    setSeverityFilter("");
    setEntityTypeFilter("");
    setPage(1);
  };

  return (
    <div className="admin-dashboard admin-audit-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Audit Log</h1>
          <p className="admin-dashboard__subtitle">
            Review authentication, user management, source, listing, and duplicate resolution activity
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadAuditLog}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!isLoading && !errorMessage && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Total Events</span>
            <span className="admin-dash__kpi-value">{kpis.total.toLocaleString()}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Success</span>
            <span className="admin-dash__kpi-value admin-dash__kpi-value--green">{kpis.successCount}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Failures</span>
            <span className={`admin-dash__kpi-value${kpis.failureCount > 0 ? " admin-dash__kpi-value--amber" : ""}`}>
              {kpis.failureCount}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Denied</span>
            <span className={`admin-dash__kpi-value${kpis.deniedCount > 0 ? " admin-dash__kpi-value--red" : ""}`}>
              {kpis.deniedCount}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Critical</span>
            <span className={`admin-dash__kpi-value${kpis.criticalCount > 0 ? " admin-dash__kpi-value--red" : ""}`}>
              {kpis.criticalCount}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Warnings</span>
            <span className={`admin-dash__kpi-value${kpis.warningCount > 0 ? " admin-dash__kpi-value--amber" : ""}`}>
              {kpis.warningCount}
            </span>
          </div>
        </section>
      )}

      <section className="admin-dash__card admin-dash__card--wide admin-audit-dash__table-card">
        <div className="admin-audit-dash__toolbar">
          <div className="admin-audit-dash__filters">
            <input
              type="search"
              className="admin-source-controls-dash__search"
              placeholder="Search user, action, entity…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <input
              type="text"
              className="admin-source-controls-dash__search admin-audit-dash__action-input"
              placeholder="Action (e.g. auth.login)"
              value={actionFilter}
              onChange={(event) => {
                setActionFilter(event.target.value);
                setPage(1);
              }}
            />
            <select
              className="admin-source-controls-dash__select"
              value={outcomeFilter}
              onChange={(event) => {
                setOutcomeFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All outcomes</option>
              <option value="success">Success</option>
              <option value="failure">Failure</option>
              <option value="denied">Denied</option>
            </select>
            <select
              className="admin-source-controls-dash__select"
              value={severityFilter}
              onChange={(event) => {
                setSeverityFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All severities</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
            <input
              type="text"
              className="admin-source-controls-dash__search admin-audit-dash__entity-input"
              placeholder="Entity type"
              value={entityTypeFilter}
              onChange={(event) => {
                setEntityTypeFilter(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="admin-audit-dash__toolbar-end">
            {hasActiveFilters && (
              <button
                type="button"
                className="admin-re-inline-link"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            )}
            <span className="admin-geocode-dash__item-count">
              {pagination.total.toLocaleString()} event{pagination.total !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {isLoading ? <AdminStatePanel variant="loading" message="Loading audit events…" /> : null}

        {!isLoading && errorMessage ? (
          <AdminStatePanel variant="error" message={errorMessage} onRetry={loadAuditLog} />
        ) : null}

        {!isLoading && !errorMessage && (
          <>
            <AdminTableContainer>
              <table className="admin-data-health-table admin-audit-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Action</th>
                    <th>Outcome</th>
                    <th>Severity</th>
                    <th>User</th>
                    <th>Entity</th>
                    <th>IP</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entries.length ? (
                    entries.map((entry) => (
                      <tr key={entry.auditLogId}>
                        <td>{formatDateTime(entry.createdAt)}</td>
                        <td><code className="admin-audit-dash__action-code">{formatAuditAction(entry.action)}</code></td>
                        <td>
                          <OutcomeBadge outcome={entry.outcome} />
                        </td>
                        <td>
                          <SeverityBadge severity={entry.severity} />
                        </td>
                        <td>{entry.userDisplayName || entry.userEmail || "—"}</td>
                        <td>
                          {entry.entityType || "—"}
                          {entry.entityId ? (
                            <div className="muted admin-re-source-code">{entry.entityId}</div>
                          ) : null}
                        </td>
                        <td>{entry.ipAddress || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-re-inline-link"
                            onClick={() => setSelectedEntry(entry)}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="muted">
                        No audit events match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </AdminTableContainer>

            <AdminTableFooter
              count={pagination.total}
              countLabel="events"
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => current + 1)}
            />
          </>
        )}
      </section>

      <AuditDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </div>
  );
};

const AdminAuditLogPage = (props) => (
  <RequirePermission permission="system.audit.view">
    <AdminAuditLogPageContent {...props} />
  </RequirePermission>
);

export default AdminAuditLogPage;
